import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {CheckBox} from '../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {useBaseEditorContext} from '../../editors/baseEditorContext';
import {EditorStateActions} from './hooks/useEditorState';
import {RuleEditorActions} from './ruleEditorActions';
import {RuleEditorConditions} from './ruleEditorConditions';
import {RuleEditorTriggers} from './ruleEditorTriggers';
import {RuleEditorState} from './state/ruleEditorState';

/*
 * Types.
 */

interface RuleEditorFormProps {
  ruleEditorState: RuleEditorState;
  actions: EditorStateActions;
}

/*
 * Styles.
 */

export const StyledRuleEditorForm = styled.div`
  width: 530px;
`;

const StyledCheckBoxWrapper = styled.div`
  margin-bottom: 20px;
`;

/*
 * Component.
 */

export const RuleEditorForm: FC<RuleEditorFormProps> = (props) => {
  const {ruleEditorState, actions} = props;
  const {isReadOnly} = useBaseEditorContext();

  return (
    <StyledRuleEditorForm>
      <RuleEditorTriggers
        triggers={ruleEditorState.ruleState.triggers}
        notDisplayingErrorCards={ruleEditorState.notDisplayingErrorCards}
        onAddTrigger={actions.onAddTrigger}
        onUpdateTrigger={actions.onUpdateTrigger}
        onDeleteTrigger={actions.onDeleteTrigger}
        onValidation={actions.onValidation}
      />
      <RuleEditorConditions
        conditions={ruleEditorState.ruleState.conditions}
        notDisplayingErrorCards={ruleEditorState.notDisplayingErrorCards}
        onAddCondition={actions.onAddCondition}
        onUpdateCondition={actions.onUpdateCondition}
        onDeleteCondition={actions.onDeleteCondition}
        onValidation={actions.onValidation}
      />
      <RuleEditorActions
        actions={ruleEditorState.ruleState.actions}
        notDisplayingErrorCards={ruleEditorState.notDisplayingErrorCards}
        onAddAction={actions.onAddAction}
        onUpdateAction={actions.onUpdateAction}
        onDeleteAction={actions.onDeleteAction}
        onValidation={actions.onValidation}
      />
      {maybeRenderProcessingCheckbox(
        isReadOnly,
        ruleEditorState.ruleState.shouldStopProcessing,
        actions.onToggleStopProcessing,
      )}
    </StyledRuleEditorForm>
  );
};

/*
 * Helpers.
 */

function maybeRenderProcessingCheckbox(
  isReadonly: boolean,
  shouldStopProcessing: boolean,
  onToggleStopProcessing: Handler,
) {
  return (
    <StyledCheckBoxWrapper>
      <CheckBox isChecked={shouldStopProcessing} isDisabled={isReadonly} onChange={onToggleStopProcessing}>
        <Trans id="rule-editor-footer-stop-processing">Stop processing other rules</Trans>
      </CheckBox>
    </StyledCheckBoxWrapper>
  );
}
